import { AiAutomationScenario } from 'shared/api/requests/aiAutomation/schemas'
import { KnowledgeEntryType } from 'shared/api/requests/aiBusinessKnowledge/schemas'
import { GoalType } from 'shared/api/requests/aiEngagementGoal/schemas'
import { analyticsService } from 'utils/services/analytics'

type Source =
  | 'first_knowlede_entry_created'
  | 'empty_state'
  | 'automation_empty_state'
  | 'knowledge_base'
  | 'automation_card'
  | 'overview'
  | 'skills'
  | 'training'
  | 'training_empty_state'
  | 'automation_editor'
  | 'tov_success_setup'

type AutomationType = 'instant_answers' | 'positive_comment_replies'

const mapScenarioToAnalyticsType = (type: AiAutomationScenario): AutomationType =>
  type === AiAutomationScenario.INSTANT_ANSWERS ? 'instant_answers' : 'positive_comment_replies'

export const AiAutomationAnalytics = {
  logKnowledgeEditorOpened: (
    payload:
      | {
          source: Extract<
            Source,
            'empty_state' | 'automation_empty_state' | 'training' | 'training_empty_state'
          >
          entry_state: 'new'
          entry_type?: KnowledgeEntryType
        }
      | {
          source: Extract<Source, 'training' | 'knowledge_base'>
          entry_state: 'new' | 'existing'
          entry_id?: string
          entry_type?: KnowledgeEntryType
        },
  ) => analyticsService.sendEvent('AI_AUTOMATION.KNOWLEDGE_EDITOR.OPENED', payload),

  logKnowledgeEntryCreated: (payload: { id: string }) =>
    analyticsService.sendEvent('AI_AUTOMATION.KNOWLEDGE_ENTRY_CREATED', payload),

  logKnowledgeEntryUpdated: (payload: { id: string }) =>
    analyticsService.sendEvent('AI_AUTOMATION.KNOWLEDGE_ENTRY_UPDATED', payload),

  logKnowledgeEntryDeleted: (payload: { id: string }) =>
    analyticsService.sendEvent('AI_AUTOMATION.KNOWLEDGE_ENTRY_DELETED', payload),

  logAutomationEditorOpened: (payload: {
    type: AiAutomationScenario
    source: Extract<
      Source,
      'automation_card' | 'first_knowlede_entry_created' | 'overview' | 'tov_success_setup'
    >
  }) =>
    analyticsService.sendEvent('AI_AUTOMATION.AUTOMATION_EDITOR_OPENED', {
      ...payload,
      type: mapScenarioToAnalyticsType(payload.type),
    }),

  logAutomationEmptyStateOpened: (payload: { type: 'instant_answers' }) =>
    analyticsService.sendEvent('AI_AUTOMATION.AUTOMATION_EDITOR_EMPTY_STATE_OPENED', payload),

  logAutomationStatusChanged: (payload: {
    type: AiAutomationScenario
    is_published: boolean
    source: Extract<Source, 'automation_editor' | 'automation_card' | 'tov_success_setup'>
  }) =>
    analyticsService.sendEvent('AI_AUTOMATION.AUTOMATION_STATUS_CHANGED', {
      ...payload,
      type: mapScenarioToAnalyticsType(payload.type),
    }),

  logInstantAnswersAssigneeChanged: (payload: { assignee: 'user' | 'group' | null }) =>
    analyticsService.sendEvent('AI_AUTOMATION.INSTANT_ANSWERS.ASSIGNEE_CHANGED', payload),

  logAiFiltersApplied: (payload: { source: 'ai_automation' | 'live_chat_banner' }) => {
    analyticsService.sendEvent('AI_AUTOMATION.AI_LIVE_CHAT_FILTERS_APPLIED', payload)
  },

  logTovRateProcessStarted: (payload: { is_first_generation: boolean }) =>
    analyticsService.sendEvent('AI_AUTOMATION.TOV_RATE_PROCESS_STARTED', payload),

  logTovSampleRated: (payload: {
    is_positive_reaction: boolean
    user_reaction: string | null
    sample_index: number
  }) => analyticsService.sendEvent('AI_AUTOMATION.TOV_SAMPLE_RATED', payload),

  logNewGoalClicked: (payload: { source: Extract<Source, 'skills' | 'overview'> }) =>
    analyticsService.sendEvent('AI_AUTOMATION.ENGAGEMENT_GOAL.NEW_GOAL_CLICKED', payload),

  logGoalEditorOpened: (payload: { id: string; goal_type: GoalType }) =>
    analyticsService.sendEvent('AI_AUTOMATION.ENGAGEMENT_GOAL.EDITOR_OPENED', payload),

  logGoalCreated: (payload: { id: string }) =>
    analyticsService.sendEvent('AI_AUTOMATION.ENGAGEMENT_GOAL.CREATED', payload),

  logGoalUpdated: (payload: { id: string }) =>
    analyticsService.sendEvent('AI_AUTOMATION.ENGAGEMENT_GOAL.UPDATED', payload),

  logGoalDeleted: (payload: { id: string }) =>
    analyticsService.sendEvent('AI_AUTOMATION.ENGAGEMENT_GOAL.DELETED', payload),

  logGoalStatusChanged: (payload: { id: string; is_published: boolean }) =>
    analyticsService.sendEvent('AI_AUTOMATION.ENGAGEMENT_GOAL.STATUS_CHANGED', payload),

  logGoalsPriorityChanged: (payload: { order: string[] }) =>
    analyticsService.sendEvent('AI_AUTOMATION.ENGAGEMENT_GOAL.PRIORITY_CHANGED', payload),

  logLeadQualPlaygroundStarted: (payload: {
    goalId: string
    action: 'start' | 'restart' | 'force_restart'
  }) => analyticsService.sendEvent('AI_AUTOMATION.ENGAGEMENT_GOAL.LEAD_QUAL_PLAYGROUND', payload),
}
